import { validateMatchInput, scoreValue } from '../utils/matchInput';
import { readAllMatches, matchSnapshot } from '../services/matchData';
import { semesterStartRating } from '../utils/semesterRatings';
// src/hooks/useRatingSystem.js
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase, getAdminClient, ensureAdminSession } from '../services/supabase';
import { calculateNewRatings, INITIAL_RATING } from '../utils/ratingCalculations';
import { useAuth } from '../contexts/AuthContext';

// 최근 N경기의 기대/실제 성적 추출 (최근 폼 R 계산용)
// ratingHistory에서 change의 부호로 승패 판단, before 레이팅 차이로 기대 승률 추정
const getRecentMatchStats = (ratingHistory, count = 5) => {
  if (!ratingHistory || ratingHistory.length < 2) return [];
  const recent = ratingHistory.slice(-count);
  return recent.map(entry => {
    const won = entry.change > 0;
    // 변동폭으로 기대 승률 역추정: change ≈ K * M * R * (actual - expected)
    // 간이 추정: 변동폭의 절대값이 K/2보다 크면 언더독 승리 → 기대 승률 낮았음
    const absChange = Math.abs(entry.change);
    const estimatedExpected = absChange > 20 ? (won ? 0.3 : 0.7) : 0.5;
    return {
      expectedScore: estimatedExpected,
      actualScore: won ? 1 : 0,
    };
  });
};

// 경기 스코어에서 승자/패자 점수 추출
const extractScores = (sets, winnerId, player1Id) => {
  if (!sets || !sets[0]) return { winnerScore: null, loserScore: null };
  const p1Won = winnerId === player1Id;
  return {
    winnerScore: p1Won ? sets[0].player1Score : sets[0].player2Score,
    loserScore: p1Won ? sets[0].player2Score : sets[0].player1Score,
  };
};

// 레이팅 기반 가중평균 계산 함수 (복식용)
const calculateWeightedTeamRating = (rating1, rating2) => {
  const total = rating1 + rating2;
  const weightedRating = (Math.pow(rating1, 2) + Math.pow(rating2, 2)) / total;
  return Math.round(weightedRating);
};

export function useRatingSystem() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const userId = user?.id || null;

  const dataRequestRef=useRef(0);
  const invalidateData=useCallback(()=>{dataRequestRef.current++;},[]);
  const fetchData = async () => {
    const request=++dataRequestRef.current;
    try {
      const [playersResponse,allMatches]=await Promise.all([
        supabase.from('players').select('*').order('name'),readAllMatches(supabase)
      ]);
      if(playersResponse.error)throw playersResponse.error;
      if(request!==dataRequestRef.current)return false;
      setLoadError(false);setPlayers(playersResponse.data||[]);setMatches(allMatches);
      return true;
    }catch(error){if(request===dataRequestRef.current)setLoadError(true);return false;}
  };

  // 초기 데이터 로딩 + visibility 기반 refetch
  useEffect(() => {
    let mounted = true;
    let lastFetchTime = 0;

    const initData = async () => {
      try {
        await fetchData();
        lastFetchTime = Date.now();
        if (mounted) {
          setIsLoading(false);
        }
      } catch (error) {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initData();

    // 탭이 다시 보일 때만 refetch (최소 60초 간격)
    const handleVisibilityChange = () => {
      if (!document.hidden && mounted && Date.now() - lastFetchTime > 60000) {
        fetchData(false);
        lastFetchTime = Date.now();
      }
    };

    // 백업 폴링: 3분 간격 (탭이 계속 열려있는 경우)
    const interval = setInterval(() => {
      if (mounted && !document.hidden) {
        fetchData(false);
        lastFetchTime = Date.now();
      }
    }, 180000);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      invalidateData();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [invalidateData]);

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => setShowSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // 재계산 동시 실행 방지 락
  const recalculatingRef = useRef(false);

  // 모든 레이팅 재계산 (경기 삭제 시 사용)
  const recalculateAllRatings = async (allPlayers, allMatches) => {
    if (recalculatingRef.current) {
      throw new Error('레이팅 재계산이 이미 진행 중입니다. 잠시 후 다시 시도해주세요.');
    }
    recalculatingRef.current=true;
    try {
      return await _recalculateAllRatingsInternal(allPlayers, allMatches);
    } finally {
      recalculatingRef.current=false;
    }
  };

  const _recalculateAllRatingsInternal = async (allPlayers, allMatches) => {
    const playersToUpdate = {};
    const processedMatches = new Set(); // 중복 방지용 Set 추가
    
    // 모든 선수 초기화
    allPlayers.forEach(p => {
      playersToUpdate[p.id] = {
        ...p,
        current_rating: semesterStartRating(p, 'singles'),
        rating_history: [],
        total_matches: 0,
        wins: 0,
        losses: 0,
        doubles_rating: semesterStartRating(p, 'doubles'),
        doubles_rating_history: [],
        doubles_total_matches: 0,
        doubles_wins: 0,
        doubles_losses: 0,
        friendly_rating: semesterStartRating(p, 'friendly'),
        friendly_rating_history: [],
        friendly_total_matches: 0,
        friendly_wins: 0,
        friendly_losses: 0
      };
    });

    // 날짜순으로 정렬, 같은 날짜는 생성 시간순
    const sortedMatches = [...allMatches].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      const createdA = new Date(a.created_at).getTime();
      const createdB = new Date(b.created_at).getTime();
      return createdA - createdB;
    });

    // 각 경기에 대해 레이팅 재계산
    for (const match of sortedMatches) {
      // 중복 처리 방지
      const matchKey = `${match.id}_${match.date}_${match.player1_id}_${match.player2_id}`;
      if (processedMatches.has(matchKey)) {
        console.warn(`Duplicate match detected and skipped: ${matchKey}`);
        continue;
      }
      processedMatches.add(matchKey);

      const isFriendly = match.match_category === 'friendly';
      
      if (match.match_type === 'doubles') {
        // 복식 경기 처리
        const player1 = playersToUpdate[match.player1_id];
        const player2 = playersToUpdate[match.player2_id];
        const player3 = playersToUpdate[match.player3_id];
        const player4 = playersToUpdate[match.player4_id];

        if (!player1 || !player2 || !player3 || !player4) continue;

        const team1Won = match.winner_id === match.player1_id;

        if (isFriendly) {
          // 친선경기 레이팅 계산
          const team1Rating = calculateWeightedTeamRating(player1.friendly_rating, player2.friendly_rating);
          const team2Rating = calculateWeightedTeamRating(player3.friendly_rating, player4.friendly_rating);
          const { winnerScore: rws, loserScore: rls } = extractScores(match.sets, match.winner_id, match.player1_id);

          const { newRating1: newTeam1Rating, newRating2: newTeam2Rating } =
            calculateNewRatings(team1Rating, team2Rating, team1Won, {
              winnerScore: rws, loserScore: rls, isDoubles: true, scoreType: match.score_type || 'tiebreak',
            });
          
          const team1Change = newTeam1Rating - team1Rating;
          const team2Change = newTeam2Rating - team2Rating;

          // 팀 A 선수들 업데이트
          [player1, player2].forEach(player => {
            const won = team1Won;
            player.friendly_total_matches++;
            if (won) player.friendly_wins++; else player.friendly_losses++;
            
            const prevRating = player.friendly_rating;
            player.friendly_rating = prevRating + team1Change;
            
            // 중복 체크: 같은 match_id가 이미 기록되었는지 확인
            const lastHistory = player.friendly_rating_history[player.friendly_rating_history.length - 1];
            if (!lastHistory || lastHistory.match_id !== match.id) {
              player.friendly_rating_history.push({
                date: match.date,
                rating: player.friendly_rating,
                change: team1Change,
                match_id: match.id // 디버깅용 match_id 추가
              });
            }
          });

          // 팀 B 선수들 업데이트
          [player3, player4].forEach(player => {
            const won = !team1Won;
            player.friendly_total_matches++;
            if (won) player.friendly_wins++; else player.friendly_losses++;
            
            const prevRating = player.friendly_rating;
            player.friendly_rating = prevRating + team2Change;
            
            // 중복 체크
            const lastHistory = player.friendly_rating_history[player.friendly_rating_history.length - 1];
            if (!lastHistory || lastHistory.match_id !== match.id) {
              player.friendly_rating_history.push({
                date: match.date,
                rating: player.friendly_rating,
                change: team2Change,
                match_id: match.id
              });
            }
          });
        } else {
          // 공식 복식 → doubles_rating 사용
          const team1Rating = calculateWeightedTeamRating(player1.doubles_rating, player2.doubles_rating);
          const team2Rating = calculateWeightedTeamRating(player3.doubles_rating, player4.doubles_rating);
          const { winnerScore: ows, loserScore: ols } = extractScores(match.sets, match.winner_id, match.player1_id);

          const { newRating1: newTeam1Rating, newRating2: newTeam2Rating } =
            calculateNewRatings(team1Rating, team2Rating, team1Won, {
              winnerScore: ows, loserScore: ols, isDoubles: true, scoreType: match.score_type || 'tiebreak',
            });

          const team1Change = newTeam1Rating - team1Rating;
          const team2Change = newTeam2Rating - team2Rating;

          // 각 선수 업데이트
          [player1, player2].forEach(player => {
            const won = team1Won;
            player.doubles_total_matches++;
            if (won) player.doubles_wins++; else player.doubles_losses++;

            const prevRating = player.doubles_rating;
            player.doubles_rating = prevRating + team1Change;

            // 중복 체크
            const lastHistory = player.doubles_rating_history[player.doubles_rating_history.length - 1];
            if (!lastHistory || lastHistory.match_id !== match.id) {
              player.doubles_rating_history.push({
                date: match.date,
                rating: player.doubles_rating,
                change: team1Change,
                match_id: match.id
              });
            }
          });

          [player3, player4].forEach(player => {
            const won = !team1Won;
            player.doubles_total_matches++;
            if (won) player.doubles_wins++; else player.doubles_losses++;

            const prevRating = player.doubles_rating;
            player.doubles_rating = prevRating + team2Change;

            // 중복 체크
            const lastHistory = player.doubles_rating_history[player.doubles_rating_history.length - 1];
            if (!lastHistory || lastHistory.match_id !== match.id) {
              player.doubles_rating_history.push({
                date: match.date,
                rating: player.doubles_rating,
                change: team2Change,
                match_id: match.id
              });
            }
          });
        }

      } else {
        // 단식 경기 처리
        const player1 = playersToUpdate[match.player1_id];
        const player2 = playersToUpdate[match.player2_id];

        if (!player1 || !player2) continue;

        const player1Won = match.winner_id === match.player1_id;

        const { winnerScore: sws, loserScore: sls } = extractScores(match.sets, match.winner_id, match.player1_id);

        if (isFriendly) {
          // 친선경기 레이팅 계산
          const { newRating1, newRating2 } = calculateNewRatings(
            player1.friendly_rating,
            player2.friendly_rating,
            player1Won,
            {
              winnerScore: sws, loserScore: sls,
              player1RecentMatches: getRecentMatchStats(player1.friendly_rating_history),
              player2RecentMatches: getRecentMatchStats(player2.friendly_rating_history),
              scoreType: match.score_type || 'tiebreak',
            }
          );

          const change1 = newRating1 - player1.friendly_rating;
          const change2 = newRating2 - player2.friendly_rating;

          // 선수1 업데이트
          player1.friendly_total_matches++;
          if (player1Won) player1.friendly_wins++; else player1.friendly_losses++;
          player1.friendly_rating = newRating1;

          // 중복 체크
          const lastHistory1 = player1.friendly_rating_history[player1.friendly_rating_history.length - 1];
          if (!lastHistory1 || lastHistory1.match_id !== match.id) {
            player1.friendly_rating_history.push({
              date: match.date,
              rating: newRating1,
              change: change1,
              match_id: match.id
            });
          }

          // 선수2 업데이트
          player2.friendly_total_matches++;
          if (!player1Won) player2.friendly_wins++; else player2.friendly_losses++;
          player2.friendly_rating = newRating2;

          // 중복 체크
          const lastHistory2 = player2.friendly_rating_history[player2.friendly_rating_history.length - 1];
          if (!lastHistory2 || lastHistory2.match_id !== match.id) {
            player2.friendly_rating_history.push({
              date: match.date,
              rating: newRating2,
              change: change2,
              match_id: match.id
            });
          }
        } else {
          // 공식경기 레이팅 계산
          const { newRating1, newRating2 } = calculateNewRatings(
            player1.current_rating,
            player2.current_rating,
            player1Won,
            {
              winnerScore: sws, loserScore: sls,
              player1RecentMatches: getRecentMatchStats(player1.rating_history),
              player2RecentMatches: getRecentMatchStats(player2.rating_history),
              scoreType: match.score_type || 'tiebreak',
            }
          );

          const change1 = newRating1 - player1.current_rating;
          const change2 = newRating2 - player2.current_rating;

          // 선수1 업데이트
          player1.total_matches++;
          if (player1Won) player1.wins++; else player1.losses++;
          player1.current_rating = newRating1;
          
          // 중복 체크
          const lastHistory1 = player1.rating_history[player1.rating_history.length - 1];
          if (!lastHistory1 || lastHistory1.match_id !== match.id) {
            player1.rating_history.push({
              date: match.date,
              rating: newRating1,
              change: change1,
              match_id: match.id
            });
          }

          // 선수2 업데이트
          player2.total_matches++;
          if (!player1Won) player2.wins++; else player2.losses++;
          player2.current_rating = newRating2;
          
          // 중복 체크
          const lastHistory2 = player2.rating_history[player2.rating_history.length - 1];
          if (!lastHistory2 || lastHistory2.match_id !== match.id) {
            player2.rating_history.push({
              date: match.date,
              rating: newRating2,
              change: change2,
              match_id: match.id
            });
          }
        }
      }
    }

    return Object.values(playersToUpdate);
  };

  // 선수 추가
  const handleAddPlayer = async (newPlayerName, setNewPlayerName, setShowAddPlayer) => {
    if (!newPlayerName.trim()) return;

    const newPlayer = {
      name: newPlayerName.trim(),
      current_rating: INITIAL_RATING,
      rating_history: [],
      total_matches: 0,
      wins: 0,
      losses: 0,
      doubles_rating: INITIAL_RATING,
      doubles_rating_history: [],
      doubles_total_matches: 0,
      doubles_wins: 0,
      doubles_losses: 0,
      friendly_rating: INITIAL_RATING,
      friendly_rating_history: [],
      friendly_wins: 0,
      friendly_losses: 0,
      friendly_total_matches: 0
    };

    try {
      const { error } = await getAdminClient()
        .from('players')
        .insert([newPlayer]);

      if (error) throw error;

      await fetchData();
      setNewPlayerName('');
      setShowAddPlayer(false);
      setShowSuccessMessage(`${newPlayer.name} 선수가 추가되었습니다!`);
    } catch (error) {
      setShowSuccessMessage('선수 추가에 실패했습니다.');
    }
  };

  const handleBatchMatchSubmit = async (matchDataList) => {
    if (!Array.isArray(matchDataList)||!matchDataList.length||matchDataList.length>100) throw new Error('경기는 한 번에 1~100건 등록할 수 있습니다.');
    matchDataList=matchDataList.map(match=>({...match,winner_id:validateMatchInput(match)}));
    try {
      // #10: 쓰기 작업 전 admin 세션 검증
      await ensureAdminSession();

      let successCount = 0;


      // DB에서 최신 선수 데이터 조회 (stale 클로저 방지, 동시 등록 레이팅 덮어쓰기 완화)
      const { data: freshPlayers, error: freshPlayersError } = await getAdminClient()
        .from('players')
        .select('*');
      if (freshPlayersError) throw freshPlayersError;
      matchDataList.forEach(match=>validateMatchInput(match,freshPlayers));
      const playersToUpdate = {};
      const playerVersions = {};
      freshPlayers.forEach(p => {
        playersToUpdate[p.id] = { ...p, rating_history:[...(p.rating_history||[])], doubles_rating_history:[...(p.doubles_rating_history||[])], friendly_rating_history:[...(p.friendly_rating_history||[])] };
        if (p.updated_at) playerVersions[p.id] = p.updated_at;
      });

      const matchesToInsert = [];
      const allPlayerUpdates = new Set();

      // 입력 순서대로 처리 (order 필드가 있으면 사용, 없으면 인덱스 사용)
      for (let i = 0; i < matchDataList.length; i++) {
        const matchData = matchDataList[i];
        
        try {
          const { player1Id, player2Id, player3Id, player4Id, date, sets, match_type, match_category, score_type, winner_id } = matchData;
          const isDoubles = match_type === 'doubles';
          const isFriendly = match_category === 'friendly';

          const winnerId = winner_id;

          // created_at을 명시적으로 설정하여 순서 보장
          const createdAt = new Date();
          createdAt.setMilliseconds(createdAt.getMilliseconds() + i); // 밀리초 단위로 순서 구분

          const newMatchData = {
            date,
            player1_id: player1Id,
            player2_id: player2Id,
            player3_id: isDoubles ? player3Id : null,
            player4_id: isDoubles ? player4Id : null,
            sets,
            winner_id: winnerId,
            match_type: match_type,
            match_category: match_category,
            score_type: score_type || 'tiebreak',
            rating_changes: {},
            created_at: createdAt.toISOString() // 순서 보장을 위한 타임스탬프
          };

          const involvedPlayerIds = isDoubles ? [player1Id, player2Id, player3Id, player4Id] : [player1Id,player2Id];
          involvedPlayerIds.forEach(id => allPlayerUpdates.add(id));

          if (isFriendly) {
            // 친선경기 레이팅 계산
            if (isDoubles) {
              const player1 = playersToUpdate[player1Id];
              const player2 = playersToUpdate[player2Id];
              const player3 = playersToUpdate[player3Id];
              const player4 = playersToUpdate[player4Id];

              if (!player1.friendly_rating) player1.friendly_rating = INITIAL_RATING;
              if (!player2.friendly_rating) player2.friendly_rating = INITIAL_RATING;
              if (!player3.friendly_rating) player3.friendly_rating = INITIAL_RATING;
              if (!player4.friendly_rating) player4.friendly_rating = INITIAL_RATING;

              const team1Rating = calculateWeightedTeamRating(player1.friendly_rating, player2.friendly_rating);
              const team2Rating = calculateWeightedTeamRating(player3.friendly_rating, player4.friendly_rating);
              const team1Won = winnerId === player1Id;
              const { winnerScore: fwScore, loserScore: flScore } = extractScores(sets, winnerId, player1Id);

              const { newRating1: newTeam1Rating, newRating2: newTeam2Rating } =
                calculateNewRatings(team1Rating, team2Rating, team1Won, {
                  winnerScore: fwScore, loserScore: flScore, isDoubles: true, scoreType: score_type || 'tiebreak',
                });
              
              const team1Change = newTeam1Rating - team1Rating;
              const team2Change = newTeam2Rating - team2Rating;

              [
                { id: player1Id, player: player1, change: team1Change, won: team1Won },
                { id: player2Id, player: player2, change: team1Change, won: team1Won },
                { id: player3Id, player: player3, change: team2Change, won: !team1Won },
                { id: player4Id, player: player4, change: team2Change, won: !team1Won }
              ].forEach(({ id, player, change, won }) => {
                player.friendly_total_matches = (player.friendly_total_matches || 0) + 1;
                if (won) {
                  player.friendly_wins = (player.friendly_wins || 0) + 1;
                } else {
                  player.friendly_losses = (player.friendly_losses || 0) + 1;
                }
                
                const prevRating = player.friendly_rating;
                player.friendly_rating = prevRating + change;
                
                if (!player.friendly_rating_history) player.friendly_rating_history = [];
                player.friendly_rating_history.push({
                  date: newMatchData.date,
                  rating: player.friendly_rating,
                  change: change
                });
                
                newMatchData.rating_changes[id] = {
                  before: prevRating,
                  after: player.friendly_rating,
                  change: change
                };
              });

            } else {
              // 단식 친선경기
              const player1 = playersToUpdate[player1Id];
              const player2 = playersToUpdate[player2Id];

              if (!player1.friendly_rating) player1.friendly_rating = INITIAL_RATING;
              if (!player2.friendly_rating) player2.friendly_rating = INITIAL_RATING;

              const prevRating1 = player1.friendly_rating;
              const prevRating2 = player2.friendly_rating;
              const { winnerScore: fsw, loserScore: fsl } = extractScores(sets, winnerId, player1Id);
              const { newRating1, newRating2 } =
                calculateNewRatings(prevRating1, prevRating2, winnerId === player1Id, {
                  winnerScore: fsw, loserScore: fsl,
                  player1RecentMatches: getRecentMatchStats(player1.friendly_rating_history),
                  player2RecentMatches: getRecentMatchStats(player2.friendly_rating_history),
                  scoreType: score_type || 'tiebreak',
                });
              const change1 = newRating1 - prevRating1;
              const change2 = newRating2 - prevRating2;

              player1.friendly_total_matches = (player1.friendly_total_matches || 0) + 1;
              if (winnerId === player1Id) {
                player1.friendly_wins = (player1.friendly_wins || 0) + 1;
              } else {
                player1.friendly_losses = (player1.friendly_losses || 0) + 1;
              }
              player1.friendly_rating = newRating1;
              if (!player1.friendly_rating_history) player1.friendly_rating_history = [];
              player1.friendly_rating_history.push({ date: newMatchData.date, rating: newRating1, change: change1 });

              player2.friendly_total_matches = (player2.friendly_total_matches || 0) + 1;
              if (winnerId === player2Id) {
                player2.friendly_wins = (player2.friendly_wins || 0) + 1;
              } else {
                player2.friendly_losses = (player2.friendly_losses || 0) + 1;
              }
              player2.friendly_rating = newRating2;
              if (!player2.friendly_rating_history) player2.friendly_rating_history = [];
              player2.friendly_rating_history.push({ date: newMatchData.date, rating: newRating2, change: change2 });

              newMatchData.rating_changes[player1Id] = { before: prevRating1, after: newRating1, change: change1 };
              newMatchData.rating_changes[player2Id] = { before: prevRating2, after: newRating2, change: change2 };
            }
          } else {
            // 공식경기 Modified Elo 계산
            if (isDoubles) {
              // 공식 복식 → doubles_rating 사용
              const player1 = playersToUpdate[player1Id];
              const player2 = playersToUpdate[player2Id];
              const player3 = playersToUpdate[player3Id];
              const player4 = playersToUpdate[player4Id];

              if (!player1.doubles_rating) player1.doubles_rating = INITIAL_RATING;
              if (!player2.doubles_rating) player2.doubles_rating = INITIAL_RATING;
              if (!player3.doubles_rating) player3.doubles_rating = INITIAL_RATING;
              if (!player4.doubles_rating) player4.doubles_rating = INITIAL_RATING;

              const team1Rating = calculateWeightedTeamRating(player1.doubles_rating, player2.doubles_rating);
              const team2Rating = calculateWeightedTeamRating(player3.doubles_rating, player4.doubles_rating);
              const team1Won = winnerId === player1Id;
              const { winnerScore, loserScore } = extractScores(sets, winnerId, player1Id);

              const { newRating1: newTeam1Rating, newRating2: newTeam2Rating } =
                calculateNewRatings(team1Rating, team2Rating, team1Won, {
                  winnerScore, loserScore, isDoubles: true, scoreType: score_type || 'tiebreak',
                });

              const team1Change = newTeam1Rating - team1Rating;
              const team2Change = newTeam2Rating - team2Rating;

              [
                { id: player1Id, player: player1, change: team1Change, won: team1Won },
                { id: player2Id, player: player2, change: team1Change, won: team1Won },
                { id: player3Id, player: player3, change: team2Change, won: !team1Won },
                { id: player4Id, player: player4, change: team2Change, won: !team1Won }
              ].forEach(({ id, player, change, won }) => {
                player.doubles_total_matches = (player.doubles_total_matches || 0) + 1;
                if (won) player.doubles_wins = (player.doubles_wins || 0) + 1;
                else player.doubles_losses = (player.doubles_losses || 0) + 1;

                const prevRating = player.doubles_rating;
                player.doubles_rating = prevRating + change;

                if (!player.doubles_rating_history) player.doubles_rating_history = [];
                player.doubles_rating_history.push({
                  date: newMatchData.date,
                  rating: player.doubles_rating,
                  change: change
                });

                newMatchData.rating_changes[id] = {
                  before: prevRating,
                  after: player.doubles_rating,
                  change: change
                };
              });

            } else {
              const player1 = playersToUpdate[player1Id];
              const player2 = playersToUpdate[player2Id];

              if (!player1.current_rating) player1.current_rating = INITIAL_RATING;
              if (!player2.current_rating) player2.current_rating = INITIAL_RATING;

              const prevRating1 = player1.current_rating;
              const prevRating2 = player2.current_rating;
              const { winnerScore, loserScore } = extractScores(sets, winnerId, player1Id);
              const p1Recent = getRecentMatchStats(player1.rating_history);
              const p2Recent = getRecentMatchStats(player2.rating_history);

              const { newRating1, newRating2 } =
                calculateNewRatings(prevRating1, prevRating2, winnerId === player1Id, {
                  winnerScore, loserScore,
                  player1RecentMatches: p1Recent,
                  player2RecentMatches: p2Recent,
                  scoreType: score_type || 'tiebreak',
                });
              const change1 = newRating1 - prevRating1;
              const change2 = newRating2 - prevRating2;

              player1.total_matches++;
              if (winnerId === player1Id) player1.wins++; else player1.losses++;
              player1.current_rating = newRating1;
              player1.rating_history.push({ date: newMatchData.date, rating: newRating1, change: change1 });

              player2.total_matches++;
              if (winnerId === player2Id) player2.wins++; else player2.losses++;
              player2.current_rating = newRating2;
              player2.rating_history.push({ date: newMatchData.date, rating: newRating2, change: change2 });

              newMatchData.rating_changes[player1Id] = { before: prevRating1, after: newRating1, change: change1 };
              newMatchData.rating_changes[player2Id] = { before: prevRating2, after: newRating2, change: change2 };
            }
          }

          matchesToInsert.push(newMatchData);
          successCount++;

        } catch (error) {
          throw new Error(`${i+1}번째 경기: ${error.message}`);
        }
      }

      // RPC를 통해 원자적으로 저장 (Optimistic Locking)
      if (matchesToInsert.length > 0) {
        const playerUpdates = Array.from(allPlayerUpdates).map(id => playersToUpdate[id]);
        const versionsToCheck = {};
        Array.from(allPlayerUpdates).forEach(id => {
          if (playerVersions[id]) versionsToCheck[id] = playerVersions[id];
        });

        const { data: rpcResult, error: rpcError } = await getAdminClient()
          .rpc('submit_match_result', {
            p_matches: matchesToInsert,
            p_player_updates: playerUpdates,
            p_player_versions: versionsToCheck
          });

        if (rpcError) throw rpcError;
        if (!rpcResult?.success) {
          if (rpcResult?.error === 'concurrent_update') {
            throw new Error('다른 관리자가 동시에 경기를 등록했습니다. 잠시 후 다시 시도해주세요.');
          }
          throw new Error(rpcResult?.error || 'RPC 실패');
        }
      }
      
      await fetchData();
      
      setShowSuccessMessage(`${successCount}건을 등록했습니다.`);

      return { success: successCount > 0, insertedMatches: matchesToInsert };
    } catch (error) {
      throw error;
    }
  };



  // 경기 등록
  // Promise 체이닝으로 제출 직렬화 (빠른 연속 클릭 시 드롭 없이 순서대로 처리)
  const submitChainRef = useRef(Promise.resolve());

  const handleMatchSubmit = async (matchData, setCurrentView, setShowSuccessMessage) => {
    validateMatchInput(matchData);
    // 이전 제출이 완료될 때까지 대기 (Promise 체이닝)
    const previousChain = submitChainRef.current;
    let resolveChain;
    submitChainRef.current = new Promise(resolve => { resolveChain = resolve; });

    await previousChain;

    const MAX_RETRIES = 3;
    try {
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          return await _handleMatchSubmitInternal(matchData, setCurrentView, setShowSuccessMessage);
        } catch (error) {
          if (error.message === 'CONCURRENT_UPDATE' && attempt < MAX_RETRIES - 1) {
            continue; // 최신 데이터로 재계산 후 재시도
          }
          throw error;
        }
      }
    } finally {
      resolveChain();
    }
  };

  const _handleMatchSubmitInternal = async (matchData, setCurrentView, setShowSuccessMessage) => {
    const { player1Id, player2Id, player3Id, player4Id, date, sets, match_type, match_category, score_type } = matchData;
    const isDoubles = match_type === 'doubles';
    const isFriendly = match_category === 'friendly';

    const winnerId=validateMatchInput(matchData);

    // #10: 쓰기 작업 전 admin 세션 검증
    await ensureAdminSession();

    // DB에서 최신 선수 데이터 조회 (stale 클로저 방지, 동시 등록 레이팅 덮어쓰기 완화)
    const { data: freshPlayers, error: freshPlayersError } = await getAdminClient()
      .from('players')
      .select('*');
    if (freshPlayersError) throw freshPlayersError;
    validateMatchInput(matchData,freshPlayers);
    const playersToUpdate = {};
    const playerVersions = {};
    freshPlayers.forEach(p => {
      playersToUpdate[p.id] = { ...p, rating_history:[...(p.rating_history||[])], doubles_rating_history:[...(p.doubles_rating_history||[])], friendly_rating_history:[...(p.friendly_rating_history||[])] };
      if (p.updated_at) playerVersions[p.id] = p.updated_at;
    });

    // 새 경기 데이터
    const newMatchData = {
      date,
      player1_id: player1Id,
      player2_id: player2Id,
      player3_id: isDoubles ? player3Id : null,
      player4_id: isDoubles ? player4Id : null,
      sets,
      winner_id: winnerId,
      match_type: match_type,
      match_category: match_category,
      score_type: score_type || 'tiebreak',
      rating_changes: {}
    };

    if (isFriendly) {
      // 친선경기 레이팅 계산
      if (isDoubles) {
        // 복식 친선경기 레이팅 처리
        const player1 = playersToUpdate[player1Id];
        const player2 = playersToUpdate[player2Id];
        const player3 = playersToUpdate[player3Id];
        const player4 = playersToUpdate[player4Id];

        // 친선 레이팅이 없으면 초기값 설정
        if (!player1.friendly_rating) player1.friendly_rating = INITIAL_RATING;
        if (!player2.friendly_rating) player2.friendly_rating = INITIAL_RATING;
        if (!player3.friendly_rating) player3.friendly_rating = INITIAL_RATING;
        if (!player4.friendly_rating) player4.friendly_rating = INITIAL_RATING;

        const team1Rating = calculateWeightedTeamRating(player1.friendly_rating, player2.friendly_rating);
        const team2Rating = calculateWeightedTeamRating(player3.friendly_rating, player4.friendly_rating);
        const team1Won = winnerId === player1Id;
        const { winnerScore: fwScore, loserScore: flScore } = extractScores(sets, winnerId, player1Id);

        const { newRating1: newTeam1Rating, newRating2: newTeam2Rating } =
          calculateNewRatings(team1Rating, team2Rating, team1Won, {
            winnerScore: fwScore, loserScore: flScore, isDoubles: true, scoreType: score_type || 'tiebreak',
          });

        const team1Change = newTeam1Rating - team1Rating;
        const team2Change = newTeam2Rating - team2Rating;

        // 각 선수의 친선 레이팅 업데이트
        [
          { id: player1Id, player: player1, change: team1Change, won: team1Won },
          { id: player2Id, player: player2, change: team1Change, won: team1Won },
          { id: player3Id, player: player3, change: team2Change, won: !team1Won },
          { id: player4Id, player: player4, change: team2Change, won: !team1Won }
        ].forEach(({ id, player, change, won }) => {
          player.friendly_total_matches = (player.friendly_total_matches || 0) + 1;
          if (won) {
            player.friendly_wins = (player.friendly_wins || 0) + 1;
          } else {
            player.friendly_losses = (player.friendly_losses || 0) + 1;
          }
          
          const prevRating = player.friendly_rating;
          player.friendly_rating = prevRating + change;
          
          if (!player.friendly_rating_history) player.friendly_rating_history = [];
          player.friendly_rating_history.push({
            date: newMatchData.date,
            rating: player.friendly_rating,
            change: change
          });
          
          newMatchData.rating_changes[id] = {
            before: prevRating,
            after: player.friendly_rating,
            change: change
          };
        });

      } else {
        // 단식 친선경기 레이팅 처리
        const player1 = playersToUpdate[player1Id];
        const player2 = playersToUpdate[player2Id];

        // 친선 레이팅이 없으면 초기값 설정
        if (!player1.friendly_rating) player1.friendly_rating = INITIAL_RATING;
        if (!player2.friendly_rating) player2.friendly_rating = INITIAL_RATING;

        const prevRating1 = player1.friendly_rating;
        const prevRating2 = player2.friendly_rating;
        const { winnerScore: fsw, loserScore: fsl } = extractScores(sets, winnerId, player1Id);
        const { newRating1, newRating2 } =
          calculateNewRatings(prevRating1, prevRating2, winnerId === player1Id, {
            winnerScore: fsw, loserScore: fsl,
            player1RecentMatches: getRecentMatchStats(player1.friendly_rating_history),
            player2RecentMatches: getRecentMatchStats(player2.friendly_rating_history),
            scoreType: score_type || 'tiebreak',
          });
        const change1 = newRating1 - prevRating1;
        const change2 = newRating2 - prevRating2;

        // 선수1 업데이트
        player1.friendly_total_matches = (player1.friendly_total_matches || 0) + 1;
        if (winnerId === player1Id) {
          player1.friendly_wins = (player1.friendly_wins || 0) + 1;
        } else {
          player1.friendly_losses = (player1.friendly_losses || 0) + 1;
        }
        player1.friendly_rating = newRating1;
        if (!player1.friendly_rating_history) player1.friendly_rating_history = [];
        player1.friendly_rating_history.push({
          date: newMatchData.date,
          rating: newRating1,
          change: change1
        });

        // 선수2 업데이트
        player2.friendly_total_matches = (player2.friendly_total_matches || 0) + 1;
        if (winnerId === player2Id) {
          player2.friendly_wins = (player2.friendly_wins || 0) + 1;
        } else {
          player2.friendly_losses = (player2.friendly_losses || 0) + 1;
        }
        player2.friendly_rating = newRating2;
        if (!player2.friendly_rating_history) player2.friendly_rating_history = [];
        player2.friendly_rating_history.push({
          date: newMatchData.date,
          rating: newRating2,
          change: change2
        });

        newMatchData.rating_changes[player1Id] = {
          before: prevRating1,
          after: newRating1,
          change: change1
        };
        newMatchData.rating_changes[player2Id] = {
          before: prevRating2,
          after: newRating2,
          change: change2
        };
      }
    } else {
      // 공식경기 레이팅 처리
      if (isDoubles) {
        // 공식 복식 → doubles_rating 사용
        const player1 = playersToUpdate[player1Id];
        const player2 = playersToUpdate[player2Id];
        const player3 = playersToUpdate[player3Id];
        const player4 = playersToUpdate[player4Id];

        if (!player1.doubles_rating) player1.doubles_rating = INITIAL_RATING;
        if (!player2.doubles_rating) player2.doubles_rating = INITIAL_RATING;
        if (!player3.doubles_rating) player3.doubles_rating = INITIAL_RATING;
        if (!player4.doubles_rating) player4.doubles_rating = INITIAL_RATING;

        const team1Rating = calculateWeightedTeamRating(player1.doubles_rating, player2.doubles_rating);
        const team2Rating = calculateWeightedTeamRating(player3.doubles_rating, player4.doubles_rating);
        const team1Won = winnerId === player1Id;
        const { winnerScore: owScore, loserScore: olScore } = extractScores(sets, winnerId, player1Id);

        const { newRating1: newTeam1Rating, newRating2: newTeam2Rating } =
          calculateNewRatings(team1Rating, team2Rating, team1Won, {
            winnerScore: owScore, loserScore: olScore, isDoubles: true, scoreType: score_type || 'tiebreak',
          });

        const team1Change = newTeam1Rating - team1Rating;
        const team2Change = newTeam2Rating - team2Rating;

        [
          { id: player1Id, player: player1, change: team1Change, won: team1Won },
          { id: player2Id, player: player2, change: team1Change, won: team1Won },
          { id: player3Id, player: player3, change: team2Change, won: !team1Won },
          { id: player4Id, player: player4, change: team2Change, won: !team1Won }
        ].forEach(({ id, player, change, won }) => {
          player.doubles_total_matches = (player.doubles_total_matches || 0) + 1;
          if (won) player.doubles_wins = (player.doubles_wins || 0) + 1;
          else player.doubles_losses = (player.doubles_losses || 0) + 1;

          const prevRating = player.doubles_rating;
          player.doubles_rating = prevRating + change;

          if (!player.doubles_rating_history) player.doubles_rating_history = [];
          player.doubles_rating_history.push({
            date: newMatchData.date,
            rating: player.doubles_rating,
            change: change
          });

          newMatchData.rating_changes[id] = {
            before: prevRating,
            after: player.doubles_rating,
            change: change
          };
        });

      } else {
        // 단식 경기 레이팅 처리
        const player1 = playersToUpdate[player1Id];
        const player2 = playersToUpdate[player2Id];

        // 현재 레이팅이 없으면 초기값 설정
        if (!player1.current_rating) player1.current_rating = INITIAL_RATING;
        if (!player2.current_rating) player2.current_rating = INITIAL_RATING;

        const prevRating1 = player1.current_rating;
        const prevRating2 = player2.current_rating;
        const { winnerScore, loserScore } = extractScores(sets, winnerId, player1Id);
        const { newRating1, newRating2 } =
          calculateNewRatings(prevRating1, prevRating2, winnerId === player1Id, {
            winnerScore, loserScore,
            player1RecentMatches: getRecentMatchStats(player1.rating_history),
            player2RecentMatches: getRecentMatchStats(player2.rating_history),
            scoreType: score_type || 'tiebreak',
          });
        const change1 = newRating1 - prevRating1;
        const change2 = newRating2 - prevRating2;

        // 선수1 업데이트
        player1.total_matches++;
        if (winnerId === player1Id) player1.wins++; else player1.losses++;
        player1.current_rating = newRating1;
        player1.rating_history.push({
          date: newMatchData.date,
          rating: newRating1,
          change: change1
        });

        // 선수2 업데이트
        player2.total_matches++;
        if (winnerId === player2Id) player2.wins++; else player2.losses++;
        player2.current_rating = newRating2;
        player2.rating_history.push({
          date: newMatchData.date,
          rating: newRating2,
          change: change2
        });

        newMatchData.rating_changes[player1Id] = {
          before: prevRating1,
          after: newRating1,
          change: change1
        };
        newMatchData.rating_changes[player2Id] = {
          before: prevRating2,
          after: newRating2,
          change: change2
        };
      }
    }

    // RPC를 통해 원자적으로 저장 (Optimistic Locking)
    try {
      const playerIds = isDoubles
        ? [player1Id, player2Id, player3Id, player4Id].filter(Boolean)
        : [player1Id, player2Id];

      const playerUpdates = playerIds.map(id => playersToUpdate[id]);
      const versionsToCheck = {};
      playerIds.forEach(id => {
        if (playerVersions[id]) versionsToCheck[id] = playerVersions[id];
      });

      const { data: rpcResult, error: rpcError } = await getAdminClient()
        .rpc(matchData.requestId ? 'approve_friendly_match_request' : 'submit_match_result', {
          ...(matchData.requestId ? { p_request_id: matchData.requestId, p_review_note: matchData.reviewNote || '' } : {}),
          p_matches: [newMatchData],
          p_player_updates: playerUpdates,
          p_player_versions: versionsToCheck
        });

      if (rpcError) throw rpcError;
      if (!rpcResult?.success) {
        if (rpcResult?.error === 'concurrent_update') {
          throw new Error('CONCURRENT_UPDATE');
        }
        throw new Error(rpcResult?.error || 'RPC 실패');
      }

      await fetchData();
      setCurrentView('matches');

      const matchTypeText = isDoubles ? '복식' : '단식';
      const categoryText = isFriendly ? '친선' : '공식';
      setShowSuccessMessage(`${categoryText} ${matchTypeText} 경기를 등록했습니다.`);
    } catch (error) {
      if (error.message === 'CONCURRENT_UPDATE') throw error; // 재시도를 위해 상위로 전파
      throw error;
    }
  };

  // Compute a revision without writes, then commit records + ratings together on the server.
  const revisionLock=useRef(false);
  const reviseMatches=async(action,ids,newScores)=>{
    if(revisionLock.current)throw new Error('경기 기록을 처리하고 있습니다. 잠시 후 다시 시도해 주세요.');
    revisionLock.current=true;
    try {
      const client=await ensureAdminSession();
      const [allMatches,response]=await Promise.all([readAllMatches(client),client.from('players').select('*')]);
      if(response.error)throw response.error;
      const allPlayers=response.data||[];
      if(!ids.length||ids.some(id=>!allMatches.some(m=>m.id===id)))throw new Error('경기 기록이 변경되었습니다. 새로고침해 주세요.');
      let sets=null;
      const revised=action==='delete'?allMatches.filter(m=>!ids.includes(m.id)):allMatches.map(m=>{
        if(m.id!==ids[0])return m;
        sets=(newScores.sets||[newScores]).map(set=>({player1Score:scoreValue(set.player1Score),player2Score:scoreValue(set.player2Score)}));
        const winner_id=validateMatchInput({date:m.date,match_type:m.match_type,match_category:m.match_category||'official',score_type:m.score_type||'tiebreak',player1Id:m.player1_id,player2Id:m.player2_id,player3Id:m.player3_id,player4Id:m.player4_id,sets},allPlayers);
        return {...m,sets,winner_id};
      });
      const updates=await recalculateAllRatings(allPlayers,revised);
      const {data,error}=await client.rpc('revise_match_records',{p_action:action,p_ids:ids,p_sets:sets,p_expected_matches:matchSnapshot(allMatches),p_player_updates:updates,p_player_versions:Object.fromEntries(allPlayers.map(p=>[p.id,p.updated_at]))});
      if(error)throw error;
      if(!data?.success)throw new Error(data?.error==='concurrent_update'?'다른 운영진이 기록을 변경했습니다. 새로고침 후 다시 시도해 주세요.':'경기 기록을 저장하지 못했습니다.');
      await fetchData();return true;
    }finally{revisionLock.current=false;}
  };
  const handleMatchDelete=async(id,close)=>{
    try{await reviseMatches('delete',[id]);close(null);setShowSuccessMessage('경기를 삭제하고 레이팅을 반영했습니다.');return true;}
    catch(error){setShowSuccessMessage(error.message);return false;}
  };
  const handleMatchEdit=async(id,scores)=>{
    await reviseMatches('edit',[id],scores);setShowSuccessMessage('점수를 수정하고 레이팅을 반영했습니다.');return true;
  };
  const handleBulkMatchDelete=async ids=>{
    try{await reviseMatches('delete',ids);setShowSuccessMessage(`${ids.length}건을 삭제하고 레이팅을 반영했습니다.`);return true;}
    catch(error){setShowSuccessMessage(error.message);return false;}
  };

  // rankings 계산 — 단식/복식/친선 별도 랭킹
  const rankings = useMemo(() => {
    const officialMatches = matches.filter(m => m.match_category !== 'friendly');
    const friendlyMatches = matches.filter(m => m.match_category === 'friendly');

    // 단식/복식 공식 매치 분리
    const officialSinglesMatches = officialMatches.filter(m => m.match_type !== 'doubles');
    const officialDoublesMatches = officialMatches.filter(m => m.match_type === 'doubles');

    const playersWithStats = players.map(player => {
      // 단식 공식 전적
      let singlesWins = player.wins || 0;
      let singlesLosses = player.losses || 0;

      if (player.total_matches === 0 || player.total_matches !== (singlesWins + singlesLosses)) {
        const playerSinglesMatches = officialSinglesMatches.filter(m =>
          m.player1_id === player.id || m.player2_id === player.id
        );
        singlesWins = 0;
        singlesLosses = 0;
        playerSinglesMatches.forEach(m => {
          if (m.winner_id === player.id) singlesWins++;
          else singlesLosses++;
        });
      }

      // 복식 공식 전적
      let doublesWins = player.doubles_wins || 0;
      let doublesLosses = player.doubles_losses || 0;

      if ((player.doubles_total_matches || 0) === 0 || (player.doubles_total_matches || 0) !== (doublesWins + doublesLosses)) {
        const playerDoublesMatches = officialDoublesMatches.filter(m =>
          m.player1_id === player.id || m.player2_id === player.id || m.player3_id === player.id || m.player4_id === player.id
        );
        doublesWins = 0;
        doublesLosses = 0;
        playerDoublesMatches.forEach(m => {
          const team1Won = m.winner_id === m.player1_id;
          let isWinner = false;
          if (team1Won && (m.player1_id === player.id || m.player2_id === player.id)) isWinner = true;
          else if (!team1Won && (m.player3_id === player.id || m.player4_id === player.id)) isWinner = true;
          if (isWinner) doublesWins++;
          else doublesLosses++;
        });
      }

      // 친선 전적
      let friendlyWins = player.friendly_wins || 0;
      let friendlyLosses = player.friendly_losses || 0;

      if (player.friendly_total_matches === 0 || player.friendly_total_matches !== (friendlyWins + friendlyLosses)) {
        const playerFriendlyMatches = friendlyMatches.filter(m =>
          m.player1_id === player.id || m.player2_id === player.id || m.player3_id === player.id || m.player4_id === player.id
        );
        friendlyWins = 0;
        friendlyLosses = 0;
        playerFriendlyMatches.forEach(m => {
          const isDoubles = m.match_type === 'doubles';
          let isWinner = false;
          if (isDoubles) {
            const team1Won = m.winner_id === m.player1_id;
            if (team1Won && (m.player1_id === player.id || m.player2_id === player.id)) isWinner = true;
            else if (!team1Won && (m.player3_id === player.id || m.player4_id === player.id)) isWinner = true;
          } else {
            if (m.winner_id === player.id) isWinner = true;
          }
          if (isWinner) friendlyWins++;
          else friendlyLosses++;
        });
      }

      return {
        ...player,
        wins: singlesWins,
        losses: singlesLosses,
        total_matches: singlesWins + singlesLosses,
        doubles_wins: doublesWins,
        doubles_losses: doublesLosses,
        doubles_total_matches: doublesWins + doublesLosses,
        friendly_wins: friendlyWins,
        friendly_losses: friendlyLosses,
        friendly_total_matches: friendlyWins + friendlyLosses
      };
    });

    // 단식 랭킹
    const singlesRankedPlayers = playersWithStats
      .filter(p => p.total_matches > 0)
      .sort((a, b) => (b.current_rating || INITIAL_RATING) - (a.current_rating || INITIAL_RATING))
      .map((player, index) => ({ ...player, rank: index + 1 }));

    const singlesUnrankedPlayers = playersWithStats
      .filter(p => p.total_matches === 0)
      .map(player => ({ ...player, rank: '-' }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // 복식 랭킹
    const doublesRankedPlayers = playersWithStats
      .filter(p => p.doubles_total_matches > 0)
      .sort((a, b) => (b.doubles_rating || INITIAL_RATING) - (a.doubles_rating || INITIAL_RATING))
      .map((player, index) => ({ ...player, doubles_rank: index + 1 }));

    const doublesUnrankedPlayers = playersWithStats
      .filter(p => p.doubles_total_matches === 0)
      .map(player => ({ ...player, doubles_rank: '-' }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // 친선 랭킹
    const friendlyRankedPlayers = playersWithStats
      .filter(p => p.friendly_total_matches > 0)
      .sort((a, b) => (b.friendly_rating || INITIAL_RATING) - (a.friendly_rating || INITIAL_RATING))
      .map((player, index) => ({ ...player, friendly_rank: index + 1 }));

    const friendlyUnrankedPlayers = playersWithStats
      .filter(p => p.friendly_total_matches === 0)
      .map(player => ({ ...player, friendly_rank: '-' }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const singlesPlayers = [...singlesRankedPlayers, ...singlesUnrankedPlayers];
    const doublesPlayers = [...doublesRankedPlayers, ...doublesUnrankedPlayers];
    const friendlyPlayers = [...friendlyRankedPlayers, ...friendlyUnrankedPlayers];

    return {
      singlesPlayers,
      doublesPlayers,
      friendlyPlayers,
      singlesRankedPlayerCount: singlesRankedPlayers.length,
      doublesRankedPlayerCount: doublesRankedPlayers.length,
      friendlyRankedPlayerCount: friendlyRankedPlayers.length
    };
  }, [players, matches]);

  // 차트 데이터 생성
  const chartData = useMemo(() => {
    const today = new Date();
    const fourteenDaysAgo = new Date(today);
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 13);

    const dateArray = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date(fourteenDaysAgo);
      date.setDate(date.getDate() + i);
      dateArray.push(date.toISOString().split('T')[0]);
    }

    return dateArray.map(date => {
      const dataPoint = { date: date.slice(5) };

      players.forEach(player => {
        const history = player.rating_history
          ?.filter(h => h.date <= date && h.rating !== null)
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        if (history && history.length > 0) {
          dataPoint[player.id] = history[0].rating;
        } else {
          dataPoint[player.id] = player.current_rating || INITIAL_RATING;
        }
      });

      return dataPoint;
    });
  }, [players]);

  return {
    players,
    matches,
    userId,
    showSuccessMessage,
    setShowSuccessMessage,
    handleAddPlayer,
    handleMatchSubmit,
    handleBatchMatchSubmit,
    handleMatchDelete,
    handleMatchEdit,
    handleBulkMatchDelete,
    rankings,
    chartData,
    isLoading,
    loadError,
    fetchData
  };
}
